import { History } from 'history';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { createMemoryHistory } from 'react-router';
import { useSelector } from '@shared/store';
import { MBSentry } from '@shared/util/sentry';
import { Navigator } from './Navigator';
import { ReactRouterContext } from './hooks';
import { browserRouteMapping, megaModalRouteMapping } from './routeMapping';
import { RouteName, RouteParamMapping } from './routeParams';

let globalHistory: History;
export const megaModalHistory = createMemoryHistory();

export function setSharedHistory(history: History) {
  globalHistory = history;
}

function getLocationDescriptorForRoute(routeName: RouteName, params: RouteParamMapping[RouteName]) {
  const newPath = browserRouteMapping[routeName]?.(params as any) ?? '';
  const [pathname, query] = newPath.split('?');
  return {
    pathname,
    search: !isNil(query) ? `?${query}` : undefined,
    // Keeping routeName and params here to be used in navigation interceptors (e.g: useNavigationBeforeLeave)
    state: {
      name: routeName,
      params,
    },
  };
}

function handleRoute(
  op: 'push' | 'replace',
  routeName: RouteName,
  params: RouteParamMapping[RouteName],
) {
  if (!!browserRouteMapping[routeName]) {
    const location = getLocationDescriptorForRoute(routeName, params);
    globalHistory[op](location);
    megaModalHistory.push('/');
  } else if (!!megaModalRouteMapping[routeName]) {
    megaModalHistory[op](megaModalRouteMapping[routeName](params as any)); // TODO: fix use of any
  } else {
    MBSentry.exception({ error: new Error(`invalid web route: ${routeName}`) });
  }
}

export function useNavigator(): Navigator {
  const navigate = useCallback<Navigator['navigate']>((routeName, params) => {
    handleRoute('push', routeName, params);
  }, []);

  const replace = useCallback<Navigator['replace']>(
    (routeName, params) => handleRoute('replace', routeName, params),
    [],
  );

  const goBack = useCallback(() => {
    const location = globalHistory.getCurrentLocation();
    if (!isEmpty(location.key)) {
      globalHistory.goBack();
    } else {
      handleRoute('replace', 'Home', undefined);
    }
  }, []);

  return useMemo<Navigator>(
    () => ({
      navigate,
      replace,
      reset: navigate,
      goBack,
      canGoBack: () => true,
      openMenu: () => megaModalHistory.push('/menu'),
      closeMenu: () => megaModalHistory.push('/'),
      getHistory: () => globalHistory,
    }),
    [goBack, navigate, replace],
  );
}

export function useNavigationFocus(): boolean {
  const hidden = useSelector(state => state.appState.isHidden);
  return !hidden;
}

export interface Location<R extends RouteName = RouteName> {
  name: R;
  params?: RouteParamMapping[R];
}

export function useNavigationBeforeLeave(
  callback: (nextLocation: Location) => boolean | Promise<boolean>,
) {
  const { router, routes } = useContext(ReactRouterContext);
  useEffect(() => {
    if (!!router) {
      router.setRouteLeaveHook(routes[routes.length - 1], route => {
        return callback(route?.state as Location);
      });
    }
  }, [callback, router, routes]);
}
